import React, { useState, useEffect, useRef } from 'react';
import { X, Plus } from 'lucide-react';
import { TrackImage } from '../../hooks/useEventManagement';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

interface EventDatesModalProps {
  selectedTrackImage: TrackImage | null;
  setSelectedTrackImage: (image: TrackImage | null) => void;
  showEventDatesModal: boolean;
  setShowEventDatesModal: (show: boolean) => void;
  eventDates: string[];
  setEventDates: (dates: string[]) => void;
  editingTitle: string;
  setEditingTitle: (title: string) => void;
  editingDescription: string;
  setEditingDescription: (description: string) => void;
  fetchTrackImages: () => Promise<void>;
  formatDateForDatabase: (date: string) => string;
}

const EventDatesModal: React.FC<EventDatesModalProps> = ({
  selectedTrackImage,
  setSelectedTrackImage,
  showEventDatesModal,
  setShowEventDatesModal,
  eventDates,
  setEventDates,
  editingTitle,
  setEditingTitle,
  editingDescription,
  setEditingDescription,
  fetchTrackImages,
  formatDateForDatabase
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newImage, setNewImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const datesContainerRef = useRef<HTMLDivElement>(null);
  const dateInputRefs = useRef<Map<number, HTMLInputElement | null>>(new Map());

  // Set image preview when selectedTrackImage changes
  useEffect(() => {
    if (selectedTrackImage?.url) {
      setImagePreview(selectedTrackImage.url);
    } else {
      setImagePreview(null);
    }
  }, [selectedTrackImage]);

  // Clean up refs when eventDates changes
  useEffect(() => {
    // Remove refs for indices that no longer exist
    const currentIndices = new Set(eventDates.map((_, index) => index));
    const refsToDelete: number[] = [];
    
    dateInputRefs.current.forEach((_, index) => {
      if (!currentIndices.has(index)) {
        refsToDelete.push(index);
      }
    });
    
    refsToDelete.forEach(index => {
      dateInputRefs.current.delete(index);
    });
  }, [eventDates.length]);

  const addDate = () => {
    const newDates = [...eventDates, ''];
    setEventDates(newDates);
    
    // Schedule scrolling to the new input after it's rendered
    setTimeout(() => {
      const newIndex = newDates.length - 1;
      const newInput = dateInputRefs.current.get(newIndex);
      
      if (newInput) {
        newInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        newInput.focus();
      } else if (datesContainerRef.current) {
        datesContainerRef.current.scrollTop = datesContainerRef.current.scrollHeight;
      }
    }, 50);
  };

  const removeDate = (index: number) => {
    const newDates = eventDates.filter((_, i) => i !== index);
    setEventDates(newDates);
    
    // Clean up the ref for the removed index
    dateInputRefs.current.delete(index);
    
    // Re-map refs for indices that shifted down
    const updatedRefs = new Map<number, HTMLInputElement | null>();
    dateInputRefs.current.forEach((ref, oldIndex) => {
      if (oldIndex < index) {
        // Indices before the removed one stay the same
        updatedRefs.set(oldIndex, ref);
      } else if (oldIndex > index) {
        // Indices after the removed one shift down by 1
        updatedRefs.set(oldIndex - 1, ref);
      }
    });
    
    dateInputRefs.current = updatedRefs;
  };

  const updateDate = (index: number, value: string) => {
    const newDates = [...eventDates];
    newDates[index] = value;
    setEventDates(newDates);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewImage(file);
      
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleEventDatesSubmit = async () => {
    if (!selectedTrackImage) return;

    setIsSubmitting(true);
    try {
      const validDates = eventDates.filter(date => date.trim() !== '');
      
      if (validDates.length === 0) {
        toast.error('Please add at least one event date');
        setIsSubmitting(false);
        return;
      }

      // Handle image update if a new image was selected
      let updatedStoragePath = selectedTrackImage.storage_path;
      
      if (newImage) {
        // Delete the old image first
        const { error: deleteError } = await supabase.storage
          .from('track-images')
          .remove([selectedTrackImage.storage_path]);
          
        if (deleteError) {
          console.warn('Error deleting old image:', deleteError);
          // Continue anyway, as we'll upload a new one
        }
        
        // Upload the new image
        const fileExt = newImage.name.split('.').pop();
        const filePath = `${selectedTrackImage.track_type}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('track-images')
          .upload(filePath, newImage);

        if (uploadError) throw uploadError;
        
        // Update the storage path
        updatedStoragePath = filePath;
      }

      // Update track image title, description, and storage path if needed
      const { error: updateError } = await supabase
        .from('track_images')
        .update({
          title: editingTitle,
          description: editingDescription,
          storage_path: updatedStoragePath
        })
        .eq('id', selectedTrackImage.id);

      if (updateError) throw updateError;

      // Get current dates from database
      const { data: currentDates, error: fetchError } = await supabase
        .from('event_dates')
        .select('event_date')
        .eq('track_image_id', selectedTrackImage.id);

      if (fetchError) throw fetchError;

      const currentDateStrings = (currentDates || []).map(d => d.event_date);
      const newDateStrings = validDates.map(date => formatDateForDatabase(date));

      // Find dates to delete (in current but not in new)
      const datesToDelete = currentDateStrings.filter(date => !newDateStrings.includes(date));
      
      // Find dates to add (in new but not in current)
      const datesToAdd = newDateStrings.filter(date => !currentDateStrings.includes(date));

      // Delete events and event_dates for removed dates
      if (datesToDelete.length > 0) {
        const { error: deleteEventsError } = await supabase
          .from('events')
          .delete()
          .eq('track_image_id', selectedTrackImage.id)
          .in('event_date', datesToDelete);

        if (deleteEventsError) throw deleteEventsError;

        const { error: deleteDatesError } = await supabase
          .from('event_dates')
          .delete()
          .eq('track_image_id', selectedTrackImage.id)
          .in('event_date', datesToDelete);

        if (deleteDatesError) throw deleteDatesError;
      }

      // Create events and event_dates for new dates
      if (datesToAdd.length > 0) {
        const eventRecords = datesToAdd.map(date => ({
          track_image_id: selectedTrackImage.id,
          name: editingTitle,
          description: editingDescription,
          event_date: date,
          registration_deadline: date,
          is_active: true
        }));

        const { error: insertError } = await supabase
          .from('events')
          .insert(eventRecords);

        if (insertError) throw insertError;

        const dateRecords = datesToAdd.map(date => ({
          track_image_id: selectedTrackImage.id,
          event_date: date
        }));

        const { error: insertDatesError } = await supabase
          .from('event_dates')
          .insert(dateRecords);

        if (insertDatesError) throw insertDatesError;
      }

      toast.success('Event details and dates updated successfully');
      setShowEventDatesModal(false);
      setSelectedTrackImage(null);
      setEventDates(['']);
      setEditingTitle('');
      setEditingDescription('');
      setNewImage(null);
      setImagePreview(null);
      fetchTrackImages();
    } catch (error) {
      console.error('Error updating event dates:', error);
      toast.error('Failed to update event dates');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showEventDatesModal || !selectedTrackImage) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">Event Details & Dates</h2>
          <button onClick={() => setShowEventDatesModal(false)} className="text-gray-400 hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Image Preview and Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Event Flyer Image
          </label>
          {imagePreview && (
            <div className="mb-3 relative">
              <img 
                src={imagePreview} 
                alt="Event flyer preview" 
                className="w-full h-40 object-cover rounded-md"
              />
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
          />
          <p className="text-xs text-gray-400 mt-1">
            Select a new image to replace the current flyer
          </p>
        </div>
        
        {/* Title and Description fields */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Event Title
            </label>
            <input
              type="text"
              value={editingTitle}
              onChange={(e) => setEditingTitle(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
              placeholder="Enter event title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Event Description
            </label>
            <textarea
              value={editingDescription}
              onChange={(e) => setEditingDescription(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white h-20"
              placeholder="Enter event description"
            />
          </div>
        </div>

        <div className="mb-4">
          <p className="text-gray-400 text-xs">
            Add dates that will block track bookings. These dates prevent regular track bookings.
          </p>
        </div>
        <div 
          ref={datesContainerRef}
          className="space-y-3 max-h-40 overflow-y-auto"
        >
          {eventDates.map((date, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="date"
                value={date}
                onChange={(e) => updateDate(index, e.target.value)}
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-md p-2 text-white"
                ref={el => dateInputRefs.current.set(index, el)}
              />
              <button
                onClick={() => removeDate(index)}
                className="text-gray-400 hover:text-primary-500"
                disabled={eventDates.length === 1}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-3">
          <button
            onClick={addDate}
            className="text-primary-500 hover:text-primary-400 text-sm font-semibold"
          >
            + Add Date
          </button>
        </div>
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => setShowEventDatesModal(false)}
            className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-md font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleEventDatesSubmit}
            disabled={isSubmitting}
            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-md font-semibold disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventDatesModal;